import React, { useMemo, useState } from "react";
import "chart.js/auto";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  LineElement,
} from "chart.js";
import { Chart } from "react-chartjs-2";
import { Dashboard, Main, PageLoader, Select } from "components";

import { ReactComponent as TaskIcon } from "icons/task.svg";
import { useQuery } from "react-query";
import { client } from "utils/api";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  Title,
  Tooltip,
  Legend
);

export const options = {
  plugins: {
    legend: {
      position: "top",
    },
    title: {
      display: false,
    },
  },
  scales: {
    x: { stacked: true },
    y: { stacked: false },
  },
};

const FINANCIAL_STATS_MIN_YEAR = 2024;

const format_pln = (value) =>
  new Intl.NumberFormat("pl-PL", {
    style: "currency",
    currency: "PLN",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(Number(value) || 0);

/** Etykieta kwoty przy końcu linii progu (Chart.js 3 plugin). */
const financialThresholdLineLabelPlugin = (text) => ({
  id: "financialThresholdLineLabel",
  afterDatasetsDraw(chart) {
    if (!text) return;
    const line_index = chart.data.datasets.findIndex((d) => d.type === "line");
    if (line_index < 0) return;
    const meta = chart.getDatasetMeta(line_index);
    if (!meta?.data?.length) return;
    const pt = meta.data[meta.data.length - 1];
    const { ctx, chartArea } = chart;
    ctx.save();
    ctx.font = "600 13px Poppins, sans-serif";
    ctx.textBaseline = "middle";
    const pad = 8;
    let x = pt.x + pad;
    let align = "left";
    ctx.textAlign = align;
    const metrics = ctx.measureText(text);
    if (x + metrics.width > chartArea.right - 4) {
      align = "right";
      x = chartArea.right - 4;
      ctx.textAlign = align;
    }
    ctx.lineWidth = 4;
    ctx.strokeStyle = "rgba(255, 255, 255, 0.85)";
    ctx.strokeText(text, x, pt.y);
    ctx.fillStyle = "rgb(255, 99, 132)";
    ctx.fillText(text, x, pt.y);
    ctx.restore();
  },
});

const FinancialCharts = () => {
  const current_calendar_year = new Date().getFullYear();
  const [selected_year, set_selected_year] = useState(current_calendar_year);

  const year_options = useMemo(() => {
    const opts = [];
    for (let y = current_calendar_year; y >= FINANCIAL_STATS_MIN_YEAR; y--) {
      opts.push({ value: y, label: String(y) });
    }
    return opts;
  }, [current_calendar_year]);

  const {
    isFetching: is_fetching,
    data: financial_stats,
  } = useQuery({
    queryKey: ["financial_stats", selected_year],
    queryFn: () =>
      client.get("/financial_results/stats", {
        params: { year: selected_year },
      }),
    select: (res) => res?.data,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
  });

  const threshold_label = "Próg obrotu premiowego";

  let chart_body = <PageLoader />;

  if (!is_fetching && financial_stats) {
    const chart_year = financial_stats.year ?? selected_year;
    const threshold_on_chart = format_pln(
      financial_stats.corrected_last_year_avg_month_value
    );

    const line_chart_data = Array(12)
      .fill(null)
      .map(
        () =>
          financial_stats.corrected_last_year_avg_month_value ||
          0
      );

    const bar_chart_data = financial_stats.total_net_income_month_histogram?.map(
      (a) => a?.[1]
    );
    const bar_chart_predicted_data =
      financial_stats.total_net_income_month_histogram?.map(
        (_, i) =>
          chart_year === current_calendar_year &&
          i === new Date().getMonth()
            ? financial_stats.predicted_value_this_month
            : 0
      );

    const labels = Array(12)
      .fill(null)
      .map(
        (_, i) =>
          `${chart_year}-${i + 1 < 10 ? `0${i + 1}` : i + 1}-01`
      );

    chart_body = (
      <Chart
        type="bar"
        style={{ padding: 20 }}
        width={window.innerWidth - 560}
        height="350px"
        plugins={[financialThresholdLineLabelPlugin(threshold_on_chart)]}
        data={{
          labels,
          datasets: [
            {
              label: "Obrót miesięczny",
              backgroundColor: "rgba(93, 120, 255, 0.2)",
              borderColor: "rgb(93, 120, 255)",
              borderWidth: 1,
              type: "bar",
              data: bar_chart_data,
              stack: "stack_1",
            },
            {
              label: "Obrót miesięczny predykcja",
              backgroundColor: "rgba(60, 179, 113, 0.2)",
              borderColor: "rgb(60, 179, 113)",
              borderWidth: 1,

              data: bar_chart_predicted_data,
              stack: "stack_1",
            },
            {
              label: threshold_label,
              type: "line",
              borderColor: "rgba(255, 99, 132, 1)",
              backgroundColor: "#fff",
              borderWidth: 2,
              fill: false,
              pointRadius: 0,
              pointHoverRadius: 0,
              data: line_chart_data,
            },
          ],
        }}
        options={options}
      />
    );
  }

  return (
    <Main
      page={{
        name: "Raporty finansowe",
        icon: <TaskIcon />,
        breadcrumbs: [{ name: "Raporty finansowe" }],
      }}
    >
      <Dashboard>
        <div style={{ padding: "0 20px 16px", maxWidth: 280 }}>
          <Select
            label="Rok"
            options={year_options}
            value={year_options.find((o) => o.value === selected_year)}
            onChange={(y) => set_selected_year(y)}
          />
        </div>
        {chart_body}
      </Dashboard>
    </Main>
  );
};

export default FinancialCharts;
